import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { api } from "../lib/api";
import { useCompare } from "../contexts/CompareContext";
import { useAuth } from "../contexts/AuthContext";
import { addCompareEntry, getCompareHistory, removeCompareEntry } from "../lib/compareHistory";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { fmtCHF, fmtKM } from "../lib/constants";
import { DealerBadge } from "../components/DealerBadge";
import {
  GitCompareArrows, X, ArrowLeft, Loader2, Building2, MessageSquare,
  Filter, AlertTriangle, Share2, History, Trash2, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { exportComparePdf } from "../lib/comparePdf";
import { useLang } from "../contexts/LanguageContext";
import CompareHubPage from "./CompareHubPage";

const ATTRIBUTES = [
  { key: "price_chf",     label: "Preis",        fmt: (v) => v != null ? fmtCHF(v) : "—", lowerIsBetter: true },
  { key: "brand",         label: "Marke",        fmt: (v) => v || "—" },
  { key: "model",         label: "Modell",       fmt: (v) => v || "—" },
  { key: "year",          label: "Jahr",         fmt: (v) => v || "—", higherIsBetter: true },
  { key: "mileage_km",    label: "Kilometer",    fmt: (v) => v != null ? `${fmtKM(v)} km` : "—", lowerIsBetter: true },
  { key: "power_kw",      label: "Leistung",     fmt: (v) => v != null ? `${v} kW (${Math.round(v * 1.36)} PS)` : "—", higherIsBetter: true },
  { key: "fuel",          label: "Treibstoff",   fmt: (v) => v ? v[0].toUpperCase() + v.slice(1) : "—" },
  { key: "transmission",  label: "Getriebe",     fmt: (v) => v ? v[0].toUpperCase() + v.slice(1) : "—" },
  { key: "drive",         label: "Antrieb",      fmt: (v) => v || "—" },
  { key: "consumption_l_100km", label: "Verbrauch",  fmt: (v) => v != null ? `${v} l/100km` : "—", lowerIsBetter: true },
  { key: "color",         label: "Farbe",        fmt: (v) => v || "—" },
  { key: "body",          label: "Karosserie",   fmt: (v) => v ? v[0].toUpperCase() + v.slice(1) : "—" },
  { key: "location",      label: "Standort",     fmt: (v, car) => v || car.canton || "—" },
];

function attrDiffers(key, cars) {
  const values = cars.map((c) => c?.[key]);
  const norm = (x) => (x == null || x === "" ? null : x);
  const set = new Set(values.map(norm));
  return set.size > 1;
}

function attrBestIndex(meta, cars) {
  if (!meta.lowerIsBetter && !meta.higherIsBetter) return -1;
  const vals = cars.map((c) => (c == null ? null : c[meta.key]));
  const numeric = vals.map((v) => (typeof v === "number" ? v : null));
  if (numeric.every((v) => v == null)) return -1;
  let bestIdx = -1;
  let bestVal = null;
  numeric.forEach((v, i) => {
    if (v == null) return;
    if (bestVal == null) { bestVal = v; bestIdx = i; return; }
    if (meta.lowerIsBetter && v < bestVal) { bestVal = v; bestIdx = i; }
    if (meta.higherIsBetter && v > bestVal) { bestVal = v; bestIdx = i; }
  });
  // If multiple cars share the best value, don't highlight a winner (it's a tie)
  const tieCount = numeric.filter((v) => v === bestVal).length;
  return tieCount > 1 ? -1 : bestIdx;
}

export default function Compare() {
  const [params] = useSearchParams();
  const navigate = useLocaleNavigate();
  const { remove } = useCompare();
  const { user } = useAuth();
  const { lang } = useLang();
  const ids = useMemo(() => params.getAll("id").slice(0, 3), [params]);
  const [cars, setCars] = useState([]); // aligned to ids; null on fetch fail
  const [loading, setLoading] = useState(true);
  const [diffOnly, setDiffOnly] = useState(false);
  const [recentHistory, setRecentHistory] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    if (ids.length === 0) {
      setCars([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(ids.map((id) => api.getListing(id).catch(() => null))).then((res) => {
      setCars(res);
      setLoading(false);
      // Fire-and-forget compare-view counter for each successfully-loaded car
      res.forEach((c) => {
        if (c && c.id) {
          api.incrementCompareView(c.id).catch(() => { /* ignore */ });
        }
      });
      // Phase 45 — Track full compare event for admin analytics (anonymous).
      // De-dup per session-set via sessionStorage to avoid re-tracking on tab focus / hot reload.
      const validIds = res.filter(Boolean).map((c) => c.id).sort();
      if (validIds.length >= 2) {
        const key = `cmp-tracked:${validIds.join("|")}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          api.trackCompareEvent(validIds).catch(() => { /* ignore */ });
        }
      }
    });
  }, [ids]);

  // SEO title + meta
  useEffect(() => {
    const valid = cars.filter(Boolean);
    if (valid.length > 0) {
      const names = valid.map((c) => `${c.brand} ${c.model}`.trim()).join(" vs ");
      document.title = `Vergleich: ${names} · CarHive`;
    } else {
      document.title = "Fahrzeugvergleich · CarHive";
    }
    // meta description
    const desc = valid.length > 0
      ? `Vergleiche ${valid.map((c) => `${c.brand} ${c.model}`.trim()).join(", ")} bei CarHive — Preis, Kilometer, Leistung, Verbrauch und mehr auf einen Blick.`
      : "Vergleiche bis zu 3 Fahrzeuge nebeneinander auf CarHive. Preis, KM, Leistung, Verbrauch und mehr.";
    let meta = document.querySelector('meta[name="description"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
    return () => {
      document.title = "CarHive";
      if (meta && prev != null) meta.setAttribute("content", prev);
    };
  }, [cars]);

  // Persist successful compare into history (localStorage + optional backend)
  useEffect(() => {
    const valid = cars.filter(Boolean);
    if (valid.length === 0 || ids.length === 0) return;
    const titles = valid.map((c) => `${c.brand || ""} ${c.model || ""}`.trim()).filter(Boolean);
    const validIds = valid.map((c) => c.id);
    addCompareEntry({ ids: validIds, titles });
    if (user) {
      api.compareHistoryAdd(validIds, titles).catch(() => { /* non-fatal */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cars]);

  // Load recent history for empty-state display (guests + users)
  useEffect(() => {
    if (ids.length > 0) return;
    if (user) {
      api.compareHistory()
        .then((r) => {
          const items = Array.isArray(r?.items) ? r.items : [];
          setRecentHistory(items.length > 0 ? items : getCompareHistory());
        })
        .catch(() => setRecentHistory(getCompareHistory()));
    } else {
      setRecentHistory(getCompareHistory());
    }
  }, [ids.length, user]);

  const removeCar = (id) => {
    remove(id);
    const remaining = ids.filter((x) => x !== id);
    const search = new URLSearchParams();
    remaining.forEach((x) => search.append("id", x));
    navigate(`/compare${remaining.length ? "?" + search.toString() : ""}`);
  };

  const validCars = cars.filter(Boolean);

  // Share compare URL — uses navigator.share if available, else clipboard.
  const shareCompare = async () => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    const names = validCars.map((c) => `${c.brand} ${c.model}`.trim()).filter(Boolean);
    const title = names.length > 0 ? `Fahrzeugvergleich: ${names.join(" vs ")}` : "Fahrzeugvergleich · CarHive";
    const text = names.length > 0 ? `Vergleiche ${names.join(", ")} auf CarHive` : "CarHive Fahrzeugvergleich";
    try {
      if (navigator.share && navigator.canShare && navigator.canShare({ url })) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // user cancelled — no toast
      // fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Vergleichslink kopiert");
    } catch {
      toast.error("Link kopieren fehlgeschlagen");
    }
  };

  // Download compare table as a clean A4 PDF (vector layout via jsPDF).
  const downloadPdf = async () => {
    if (pdfBusy || validCars.length === 0) return;
    setPdfBusy(true);
    const PDF_I18N = {
      de: { start: "PDF wird vorbereitet …", ok: "PDF heruntergeladen", err: "PDF-Erstellung fehlgeschlagen" },
      fr: { start: "Préparation du PDF…", ok: "PDF téléchargé", err: "Échec de la création du PDF" },
      it: { start: "Preparazione PDF…", ok: "PDF scaricato", err: "Creazione PDF fallita" },
      en: { start: "Preparing PDF…", ok: "PDF downloaded", err: "PDF generation failed" },
    };
    const m = PDF_I18N[lang] || PDF_I18N.de;
    const toastId = toast.loading(m.start);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      await exportComparePdf({ cars: validCars, attributes: ATTRIBUTES, locale: lang, apiBase });
      toast.success(m.ok, { id: toastId });
    } catch (e) {
      console.error("PDF export failed:", e);
      toast.error(m.err, { id: toastId });
    } finally {
      setPdfBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-zinc-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (ids.length === 0) {
    // Phase 50.1 — Empty state replaced by full Compare Hub (better UX + SEO landing for /en/compare).
    return <CompareHubPage />;
  }

  const rows = diffOnly ? ATTRIBUTES.filter((a) => attrDiffers(a.key, validCars)) : ATTRIBUTES;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 pb-24" data-testid="compare-page">
      <Link to="/listings" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-400">
        <ArrowLeft className="h-3.5 w-3.5" /> Zurück zu Inseraten
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">CarHive · Vergleich</p>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">
            Fahrzeugvergleich ({validCars.length}/3)
          </h1>
        </div>
        {validCars.length > 1 && (
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={downloadPdf}
              disabled={pdfBusy}
              className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900 disabled:opacity-60"
              data-testid="compare-pdf-button"
            >
              {pdfBusy
                ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <FileDown className="mr-1.5 h-3.5 w-3.5" />}
              PDF
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={shareCompare}
              className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900"
              data-testid="compare-share-button"
            >
              <Share2 className="mr-1.5 h-3.5 w-3.5" />
              Teilen
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDiffOnly((v) => !v)}
              className={`border-zinc-800 bg-transparent text-white hover:bg-zinc-900 ${diffOnly ? "border-red-500 bg-red-600/10 text-red-300" : ""}`}
              data-testid="compare-toggle-diff"
            >
              <Filter className="mr-1.5 h-3.5 w-3.5" />
              {diffOnly ? "Alle Felder" : "Nur Unterschiede"}
            </Button>
          </div>
        )}
      </div>

      {/* Header row: car cards */}
      <div className={`mt-6 grid gap-3 ${validCars.length === 1 ? "grid-cols-1" : validCars.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
        {cars.map((car, idx) => {
          const id = ids[idx];
          if (!car) {
            return (
              <Card key={id} className="border-red-500/30 bg-red-500/5 p-4 text-center text-xs text-red-300" data-testid={`compare-card-missing-${id}`}>
                <AlertTriangle className="mx-auto h-5 w-5" />
                <p className="mt-2 font-semibold">Inserat nicht verfügbar</p>
                <button onClick={() => removeCar(id)} className="mt-2 text-[11px] text-red-400 hover:text-red-300">Entfernen</button>
              </Card>
            );
          }
          return (
            <Card key={car.id} className="relative overflow-hidden border-zinc-800 bg-zinc-950" data-testid={`compare-card-${car.id}`}>
              <button
                onClick={() => removeCar(car.id)}
                className="absolute right-2 top-2 z-10 rounded-full bg-black/60 p-1 text-zinc-300 backdrop-blur-md hover:bg-red-600 hover:text-white"
                title="Entfernen"
                data-testid={`compare-remove-${car.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
              {car.images?.[0] && (
                <Link to={`/listings/${car.id}`} className="block">
                  <img src={car.images[0]} alt={car.title} className="aspect-[4/3] w-full object-cover" />
                </Link>
              )}
              <div className="p-3">
                <div className="flex items-center gap-1.5">
                  {car.seller_dealer && <DealerBadge dealer={car.seller_dealer} />}
                </div>
                <Link to={`/listings/${car.id}`} className="mt-1 block">
                  <p className="line-clamp-1 font-display text-sm font-semibold text-white hover:text-red-300 sm:text-base">{car.title}</p>
                </Link>
                <p className="mt-1 font-display text-lg font-bold text-red-400">{fmtCHF(car.price_chf)}</p>
                {car.owner_id && (
                  <Button asChild size="sm" className="mt-3 w-full bg-red-600 hover:bg-red-500" data-testid={`compare-inquire-${car.id}`}>
                    <Link to={`/listings/${car.id}#inquire`}>
                      <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Anfrage senden
                    </Link>
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Compare table */}
      <div className="mt-6 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            Keine Unterschiede in den Standard-Attributen. Schalte um auf „Alle Felder", um alles zu sehen.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="compare-table">
            <tbody className="divide-y divide-zinc-800">
              {rows.map((meta) => {
                const differs = attrDiffers(meta.key, validCars);
                const bestIdx = differs ? attrBestIndex(meta, validCars) : -1;
                return (
                  <tr key={meta.key} className={differs ? "bg-amber-500/[0.03]" : ""} data-testid={`compare-row-${meta.key}`}>
                    <th className="w-32 border-r border-zinc-800 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:w-40 sm:px-4">
                      {meta.label}
                    </th>
                    {cars.map((car, i) => {
                      if (!car) return <td key={i} className="px-3 py-2.5 text-xs text-zinc-600 sm:px-4">—</td>;
                      const validIdx = validCars.indexOf(car);
                      const isBest = bestIdx >= 0 && validIdx === bestIdx;
                      return (
                        <td
                          key={car.id}
                          className={`px-3 py-2.5 align-top text-xs sm:px-4 sm:text-sm ${
                            isBest
                              ? "font-semibold text-emerald-300"
                              : differs
                                ? "text-amber-100"
                                : "text-zinc-300"
                          }`}
                        >
                          {meta.fmt(car[meta.key], car)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Special row: Dealer */}
              <tr data-testid="compare-row-dealer">
                <th className="w-32 border-r border-zinc-800 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:w-40 sm:px-4">
                  Verkäufer
                </th>
                {cars.map((car, i) => {
                  if (!car) return <td key={i} className="px-3 py-2.5 text-xs text-zinc-600 sm:px-4">—</td>;
                  const sd = car.seller_dealer;
                  return (
                    <td key={car.id} className="px-3 py-2.5 text-xs sm:px-4">
                      {sd ? (
                        <DealerBadge dealer={sd} />
                      ) : (
                        <span className="inline-flex items-center gap-1 text-zinc-500">
                          <Building2 className="h-3 w-3" /> Privat
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
